import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

export default function PipelineConnectorOverlay({ wrapperRef, nodeRefs, anchorRefs, nodes, nodeById, connections }) {
    const DOT_RADIUS = 5;
    const CHEVRON_W = 2;
    const CHEVRON_H = 2;
    const CHEVRON_STROKE = 1;
    const CHEVRON_OFFSET_X = 1.25;
    const CHEVRON_OFFSET_Y = 0;
    const PATH_COLOR = 'rgb(209 213 219)';
    const DOT_FILL = 'rgb(209 213 219)';
    const CHEVRON_COLOR = 'white';
    const PATH_WIDTH = 3;
    const CORNER_RADIUS = 10;
    const DASHED_OPACITY = 0.45;

    const [layout, setLayout] = useState({ width: 0, height: 0, rects: {} });

    const setupThrottledListeners = (measureFn) => {
        let frame = null;

        const onChange = () => {
            if (frame) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                frame = null;
                measureFn();
            });
        };

        return {
            add() {
                window.addEventListener('resize', onChange);
                window.addEventListener('scroll', onChange, { passive: true });
            },
            remove() {
                window.removeEventListener('resize', onChange);
                window.removeEventListener('scroll', onChange);
                if (frame) {
                    cancelAnimationFrame(frame);
                    frame = null;
                }
            }
        };
    };

    const measure = () => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const wrect = wrapper.getBoundingClientRect();
        const rects = {};

        for (const n of nodes) {
            const outerEl = nodeRefs.current[n.id];
            if (!outerEl) continue;

            const anchorEl = anchorRefs?.current?.[n.id] || outerEl;
            const r = anchorEl.getBoundingClientRect();

            const left = r.left - wrect.left;
            const top = r.top - wrect.top;

            rects[n.id] = {
                x: left,
                y: top,
                w: r.width,
                h: r.height,
                center: { x: left + r.width / 2, y: top + r.height / 2 },
                leftMid: { x: left, y: top + r.height / 2 },
                rightMid: { x: left + r.width, y: top + r.height / 2 },
                topMid: { x: left + r.width / 2, y: top },
                bottomMid: { x: left + r.width / 2, y: top + r.height },
                bottom: top + r.height,
            };
        }

        setLayout({ width: wrect.width, height: wrect.height, rects });
    };

    useLayoutEffect(() => { measure(); }, [nodes]);

    useEffect(() => {
        if (!wrapperRef.current) return;

        const ro = new ResizeObserver(() => measure());
        ro.observe(wrapperRef.current);

        const throttled = setupThrottledListeners(measure);
        throttled.add();

        return () => {
            ro.disconnect();
            throttled.remove();
        };
    }, [wrapperRef]);

    const paths = useMemo(() => {
        if (!layout.width || !layout.height) return [];

        const fromSideMap = {};
        const toSideMap = {};

        connections.forEach((c, idx) => {
            if (!fromSideMap[c.from]) fromSideMap[c.from] = {};
            if (!fromSideMap[c.from][c.fromSide || 'center']) fromSideMap[c.from][c.fromSide || 'center'] = [];
            fromSideMap[c.from][c.fromSide || 'center'].push(idx);

            if (!toSideMap[c.to]) toSideMap[c.to] = {};
            if (!toSideMap[c.to][c.toSide || 'center']) toSideMap[c.to][c.toSide || 'center'] = [];
            toSideMap[c.to][c.toSide || 'center'].push(idx);
        });

        const getAnchored = (rect, side, index = 0, total = 1) => {
            const t = total > 1 ? (index + 1) / (total + 1) : 0.5;

            switch (side) {
                case 'left': return { x: rect.x, y: rect.y + rect.h * t };
                case 'right': return { x: rect.x + rect.w, y: rect.y + rect.h * t };
                case 'top': return { x: rect.x + rect.w * t, y: rect.y };
                case 'bottom': return { x: rect.x + rect.w * t, y: rect.y + rect.h };
                case 'center':
                default: return { x: rect.center.x, y: rect.center.y };
            }
        };

        const getAnchor = (rect, side) => {
            switch (side) {
                case 'left': return rect.leftMid;
                case 'right': return rect.rightMid;
                case 'top': return rect.topMid;
                case 'bottom': return rect.bottomMid;
                case 'center':
                default: return rect.center;
            }
        };

        const toRoundedPath = (points, radius = CORNER_RADIUS) => {
            if (!points || points.length === 0) return '';
            if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

            const dir = (a, b) => ({ x: Math.sign(b.x - a.x), y: Math.sign(b.y - a.y) });

            let d = `M ${points[0].x} ${points[0].y}`;

            for (let i = 1; i < points.length; i++) {
                const prev = points[i - 1];
                const curr = points[i];
                const next = points[i + 1];

                if (!next) {
                    d += `L ${curr.x} ${curr.y}`;
                    break;
                }

                const v1 = dir(prev, curr);
                const v2 = dir(curr, next);

                if (v1.x === v2.x && v1.y === v2.y) {
                    d += `L ${curr.x} ${curr.y}`;
                    continue;
                }

                const seg1 = Math.hypot(curr.x - prev.x, curr.y - prev.y);
                const seg2 = Math.hypot(next.x - curr.x, next.y - curr.y);
                const cut = Math.min(radius, seg1 / 2, seg2 / 2);

                const cornerIn = { x: curr.x - v1.x * cut, y: curr.y - v1.y * cut };
                const cornerOut = { x: curr.x + v2.x * cut, y: curr.y + v2.y * cut };

                d += ` L ${cornerIn.x} ${cornerIn.y} Q ${curr.x} ${curr.y} ${cornerOut.x} ${cornerOut.y}`;
            }

            return d;
        };

        const result = [];

        let marginUnder = 24;
        if (wrapperRef.current) {
            const style = getComputedStyle(wrapperRef.current);
            const rowGap = parseInt(style.rowGap || style.gap || '24', 10);
            if (!Number.isNaN(rowGap)) marginUnder = Math.max(8, Math.round(rowGap / 2));
        }

        for (let idx = 0; idx < connections.length; idx++) {
            const c = connections[idx];
            const Ameta = nodeById[c.from];
            const Bmeta = nodeById[c.to];
            const A = layout.rects[c.from];
            const B = layout.rects[c.to];
            if (!A || !B || !Ameta || !Bmeta) continue;

            const fromList = fromSideMap[c.from]?.[c.fromSide || 'center'] || [idx];
            const toList = toSideMap[c.to]?.[c.toSide || 'center'] || [idx];
            const fromIndex = Math.max(0, fromList.indexOf(idx));
            const toIndex = Math.max(0, toList.indexOf(idx));

            const start = fromList.length > 1 ? getAnchored(A, c.fromSide || 'center', fromIndex, fromList.length) : getAnchor(A, c.fromSide || 'center');
            const end = toList.length > 1 ? getAnchored(B, c.toSide || 'center', toIndex, toList.length) : getAnchor(B, c.toSide || 'center');

            const route = c.route || 'auto';
            const isSameRow = Ameta.row === Bmeta.row;
            const isBottomToBottom = c.fromSide === 'bottom' && c.toSide === 'bottom';

            let pts;

            if (route === 'under' || (route === 'auto' && isBottomToBottom && isSameRow)) {
                const rowBottom = Math.max(A.bottom, B.bottom);
                const yUnder = rowBottom + marginUnder;
                pts = [
                    { x: start.x, y: start.y },
                    { x: start.x, y: yUnder },
                    { x: end.x, y: yUnder },
                    { x: end.x, y: end.y },
                ];
            } else if (route === 'hfirst') {
                pts = [
                    { x: start.x, y: start.y },
                    { x: end.x, y: start.y },
                    { x: end.x, y: end.y },
                ];
            } else if (route === 'vfirst') {
                pts = [
                    { x: start.x, y: start.y },
                    { x: start.x, y: end.y },
                    { x: end.x, y: end.y },
                ];
            } else {
                pts = Math.abs(start.x - end.x) > Math.abs(start.y - end.y)
                    ? [{ x: start.x, y: start.y }, { x: end.x, y: start.y }, { x: end.x, y: end.y }]
                    : [{ x: start.x, y: start.y }, { x: start.x, y: end.y }, { x: end.x, y: end.y }];
            }

            const prev = pts[pts.length - 2] || start;
            const segAngleRad = Math.atan2(end.y - prev.y, end.x - prev.x);

            const inwardDegMap = { right: 180, left: 0, top: 90, bottom: -90 };
            const forcedAngleDeg = inwardDegMap[c?.toSide || ''];
            const angleDeg = typeof forcedAngleDeg === 'number' ? forcedAngleDeg : segAngleRad * (180 / Math.PI);
            const angleRad = angleDeg * (Math.PI / 180);

            const offsetX = CHEVRON_OFFSET_X * Math.cos(angleRad) - CHEVRON_OFFSET_Y * Math.sin(angleRad);
            const offsetY = CHEVRON_OFFSET_X * Math.sin(angleRad) + CHEVRON_OFFSET_Y * Math.cos(angleRad);

            result.push({
                d: toRoundedPath(pts),
                start,
                end,
                angle: angleDeg,
                offsetX,
                offsetY,
                dashed: !!c.dashed,
            });
        }

        return result;
    }, [connections, layout, nodeById, wrapperRef]);

    if (!layout.width || !layout.height) return null;

    return (
        <svg className='pointer-events-none absolute inset-0' width={layout.width} height={layout.height} viewBox={`0 0 ${layout.width} ${layout.height}`}>
            {paths.map((p, i) => (
                <g key={i}>
                    <path
                        id={`path-${i}`}
                        d={p.d}
                        fill='none'
                        stroke={PATH_COLOR}
                        strokeWidth={PATH_WIDTH}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeDasharray={p.dashed ? '8 6' : undefined}
                        opacity={p.dashed ? DASHED_OPACITY : 1}
                    />

                    <circle cx={p.start.x} cy={p.start.y} r={DOT_RADIUS} fill={DOT_FILL} />
                    <circle cx={p.end.x} cy={p.end.y} r={DOT_FILL ? DOT_RADIUS : 0} fill={DOT_FILL} />

                    <g transform={`translate(${p.end.x + p.offsetX}, ${p.end.y + p.offsetY}) rotate(${p.angle})`} opacity={p.dashed ? DASHED_OPACITY : 1}>
                        <path
                            d={`M ${-CHEVRON_W} ${-CHEVRON_H} L 0 0 L ${-CHEVRON_W} ${CHEVRON_H}`}
                            stroke={CHEVRON_COLOR}
                            strokeWidth={CHEVRON_STROKE}
                            fill='none'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                    </g>

                    {connections[i]?.label && (
                        <text fontSize="10" fill="#6b7280" opacity={p.dashed ? DASHED_OPACITY : 1}>
                            <textPath href={`#path-${i}`} startOffset="50%" textAnchor="middle">
                                {connections[i].label}
                            </textPath>
                        </text>
                    )}
                </g>
            ))}
        </svg>
    );
}